import React from 'react'
import { CATEGORY_OPTIONS } from '../../common/categories'
import {
  isLeadGenFullbleedLayoutKey,
  LEAD_GEN_FULLBLEED_STYLE_PREFIX,
  leadGenFullbleedTemplateStyleForCategory,
} from '../../common/leadGenFullbleedKeys'
import { buildLayoutConfig, COMMON_LAYOUT_EDITABLE_FIELDS, IntroStatsPanel, LayoutSections, splitHighlightSection } from './layoutShared'

const LEAD_ANCHOR_ID = 'sgez-category-landing-lead-anchor'

/** 히어로 하단 뱃지(icon-grid 등)·본문과 맞춘 가로 폭 (기존 1200px → 더 넓게) */
const CONTENT_BAND_CLASS = 'mx-auto w-full max-w-[min(1600px,calc(100vw-2rem))]'

/** 히어로 상단 제목·부제 좌측 여백 (큰 화면에서 약 15rem) */
const HERO_HEADLINE_PL = 'pl-4 md:pl-8 lg:pl-[15rem]'

export { isLeadGenFullbleedLayoutKey, LEAD_GEN_FULLBLEED_STYLE_PREFIX, leadGenFullbleedTemplateStyleForCategory }

const DEFAULT_CONFIG = {
  heroBandBackgroundColor: '#ECEAFF',
  detailStripBackgroundColor: '#F7F7F7',
  heroDetailImageFileId: '',
  formBorderColor: '#f47321',
  formBorderWidth: 2,
  highlightsBackgroundColor: '#f8fafc',
  recommendedTitle: '추천상품',
  /** PC 고정 폼 세로 위치 (한화 키워드 LP iframe 대략 top:170px 에 맞춤) */
  fixedFormTop: '170px',
}

const FULLBLEED_DESCRIPTION =
  '한화식 키워드 LP에 가깝습니다(풀폭 히어로 이미지 위 좌측 타이틀 오버레이, PC 우측 고정 폼). 템플릿 1 일반형과 달리 둥근 카드형 히어로·이미지 아래 파란 스크롤 버튼은 없습니다. 외부 청약 iframe 대신 자사 폼을 씁니다.'

const FULLBLEED_EDITABLE_FIELDS = [
  ...COMMON_LAYOUT_EDITABLE_FIELDS,
  { key: 'heroBandBackgroundColor', label: '히어로 상단 밴드 배경색', placeholder: '#ECEAFF' },
  { key: 'detailStripBackgroundColor', label: '상세 띠(2단) 배경색', placeholder: '#F7F7F7' },
  { key: 'heroDetailImageFileId', label: '상세 띠 이미지 fileId', placeholder: '업로드 파일 ID' },
  { key: 'fixedFormTop', label: 'PC 고정 폼 top 오프셋', placeholder: '170px' },
]

function LeadGenFullbleedLayout({
  mode = 'public',
  heroTitle,
  heroSubtitle,
  heroImageFileId,
  sections = [],
  formEnabled = true,
  introStats = [],
  customConfig = {},
  resolveAssetUrl,
  formCard = null,
  recommendedProductsCard = null,
  previewBanner = null,
  surveyModal = null,
  surveyDoneModal = null,
}) {
  const layoutStyleKey = isLeadGenFullbleedLayoutKey(customConfig?.templateStyle)
    ? customConfig.templateStyle
    : LEAD_GEN_FULLBLEED_STYLE_PREFIX
  const config = buildLayoutConfig(layoutStyleKey, customConfig, DEFAULT_CONFIG)
  const isLive = mode === 'live'
  const heroImageUrl = heroImageFileId ? resolveAssetUrl(heroImageFileId) : ''
  const detailImageUrl = config.heroDetailImageFileId ? resolveAssetUrl(config.heroDetailImageFileId) : ''

  const { highlightSection, bodySections } = splitHighlightSection(sections, true)

  const formInner = formEnabled && formCard ? formCard : null

  const fixedFormTopValue = config.fixedFormTop || DEFAULT_CONFIG.fixedFormTop
  /** PC 고정 폼+추천상품 스택: 뷰포트 높이를 넘으면 스크롤 (추천상품이 잘리지 않도록) */
  const fixedFormStackStyle = {
    top: fixedFormTopValue,
    maxHeight: `calc(100dvh - (${fixedFormTopValue}) - 1.25rem)`,
  }

  /* 폼 카드(자식)에만 테두리·라운드가 있으므로 래퍼는 그림자만 둬 이중 박스를 피함 */
  const formShellMobile = formInner ? (
    <div id={LEAD_ANCHOR_ID} className="rounded-[28px] overflow-hidden shadow-xl">
      {formInner}
    </div>
  ) : null

  const formShellDesktop = formInner ? (
    <div className="rounded-[28px] overflow-hidden shadow-xl">
      {formInner}
    </div>
  ) : null

  return (
    <div className="min-h-full bg-white text-slate-900 relative">
      {previewBanner}

      <div style={{ backgroundColor: config.heroBandBackgroundColor }} className="w-full">
        {heroImageUrl ? (
          <div className="relative w-full">
            <img
              src={heroImageUrl}
              alt={heroTitle || '랜딩 히어로 이미지'}
              className="block w-full h-auto max-h-[min(720px,85vh)] object-cover object-center rounded-none"
            />
            {(heroTitle || heroSubtitle) ? (
              <>
                {/* 한화식 키워드 LP: 좌측 가독용 그라데이션 + 이미지 위 타이틀 */}
                <div
                  className="pointer-events-none absolute inset-0 bg-gradient-to-r from-black/65 via-black/35 to-transparent"
                  aria-hidden="true"
                />
                <div
                  className={`absolute inset-0 flex items-center ${isLive ? '' : 'xl:pr-[min(400px,42vw)]'}`}
                >
                  <div className={`relative z-10 py-8 md:py-10 pr-4 md:pr-6 ${HERO_HEADLINE_PL} ${CONTENT_BAND_CLASS} ${isLive ? '' : 'xl:pr-[400px]'}`}>
                    {heroTitle ? (
                      <h1
                        className="max-w-xl whitespace-pre-line text-2xl font-extrabold leading-tight tracking-tight text-white drop-shadow-md md:text-3xl lg:text-4xl"
                        style={config.heroTextColor ? { color: config.heroTextColor } : undefined}
                      >
                        {heroTitle}
                      </h1>
                    ) : null}
                    {heroSubtitle ? (
                      <p
                        className="mt-3 max-w-xl text-base leading-relaxed text-white/90 drop-shadow md:mt-4 md:text-lg whitespace-pre-line"
                        style={config.heroSubtitleColor ? { color: config.heroSubtitleColor } : undefined}
                      >
                        {heroSubtitle}
                      </p>
                    ) : null}
                  </div>
                </div>
              </>
            ) : null}
          </div>
        ) : (
          <div className={`py-16 md:py-20 pr-4 text-left md:pr-6 ${HERO_HEADLINE_PL} ${CONTENT_BAND_CLASS} ${isLive ? '' : 'xl:pr-[400px]'}`}>
            {heroTitle && (
              <h1 className="whitespace-pre-line text-4xl md:text-5xl font-extrabold tracking-tight" style={{ color: config.heroTextColor || '#0f172a' }}>
                {heroTitle}
              </h1>
            )}
            {heroSubtitle && (
              <p
                className="mt-4 text-lg md:text-xl whitespace-pre-line"
                style={{ color: config.heroSubtitleColor || '#475569' }}
              >
                {heroSubtitle}
              </p>
            )}
          </div>
        )}
      </div>

      {detailImageUrl ? (
        <div style={{ backgroundColor: config.detailStripBackgroundColor }} className="w-full">
          <img src={detailImageUrl} alt="" className="block w-full h-auto object-cover object-center rounded-none" />
        </div>
      ) : null}

      {(formEnabled || recommendedProductsCard) && (
        <div className={`mx-auto max-w-md px-4 py-6 space-y-4 ${isLive ? '' : 'xl:hidden'}`}>
          {formShellMobile}
          {recommendedProductsCard}
        </div>
      )}

      {!isLive && (formEnabled || recommendedProductsCard) && (
        <div
          className="hidden xl:flex fixed right-6 z-30 w-[min(340px,calc(100vw-2rem))] flex-col gap-3 items-stretch overflow-y-auto overscroll-contain pb-3 [scrollbar-gutter:stable]"
          style={fixedFormStackStyle}
        >
          {formShellDesktop ? <div className="shrink-0">{formShellDesktop}</div> : null}
          {recommendedProductsCard ? <div className="shrink-0">{recommendedProductsCard}</div> : null}
        </div>
      )}

      {highlightSection && (
        <div style={{ backgroundColor: config.highlightsBackgroundColor }} className="border-y border-slate-200 px-4 py-8">
          <div className={`${CONTENT_BAND_CLASS} ${isLive ? '' : 'xl:pr-[400px]'}`}>
            <LayoutSections sections={[highlightSection]} resolveAssetUrl={resolveAssetUrl} variant="compact" />
          </div>
        </div>
      )}

      <div className="px-4 py-12 md:py-14 bg-white">
        <div className={`${CONTENT_BAND_CLASS} ${isLive ? 'space-y-4' : 'xl:pr-[400px] space-y-6'}`}>
          <LayoutSections sections={bodySections} resolveAssetUrl={resolveAssetUrl} variant={isLive ? 'compact' : 'default'} />
          <IntroStatsPanel introStats={introStats} />
        </div>
      </div>

      {surveyModal}
      {surveyDoneModal}
    </div>
  )
}

/** 구버전 DB에 templateStyle 이 lead-gen-fullbleed 로만 저장된 경우 */
export const leadGenFullbleedLegacyLayout = {
  key: LEAD_GEN_FULLBLEED_STYLE_PREFIX,
  label: '6 - 풀블리드 전환형',
  description: FULLBLEED_DESCRIPTION,
  editableConfigFields: FULLBLEED_EDITABLE_FIELDS,
  normalizeCustomConfig: (customConfig) => buildLayoutConfig(LEAD_GEN_FULLBLEED_STYLE_PREFIX, customConfig, DEFAULT_CONFIG),
  Component: LeadGenFullbleedLayout,
}

export const leadGenFullbleedLayoutsByCategory = CATEGORY_OPTIONS.map(({ code, label }) => ({
  key: leadGenFullbleedTemplateStyleForCategory(code),
  label: `6 - 풀블리드 (${label})`,
  description: FULLBLEED_DESCRIPTION,
  supportsCategory: (c) => c === code,
  editableConfigFields: FULLBLEED_EDITABLE_FIELDS,
  normalizeCustomConfig: (customConfig) => buildLayoutConfig(leadGenFullbleedTemplateStyleForCategory(code), customConfig, DEFAULT_CONFIG),
  Component: LeadGenFullbleedLayout,
}))
